import { useRef, useState, useCallback } from 'react';
import { ComposerPrimitive, ThreadPrimitive } from '@assistant-ui/react';
import { Send, Square, Paperclip, X, FileText, FileImage, File as FileIcon, Loader2, Mic } from 'lucide-react';
import { useEmbeddedIvyChatStore } from '@/stores/embeddedIvyChatStore';
import api from '@/services/api';
import { ReviewChipStack } from './ReviewChipStack';
import { OutlineEditChipStack } from './OutlineEditChipStack';
import { studioBOn } from '@/lib/studioB/flag';
import { IconTipB } from '@/components/studio/IconTipB';
import type { SceneScope } from '@/lib/ivyChat/useSceneScope';
import { cn } from '@/lib/utils';
import { FootageChips } from '@/components/chat/FootageChips';
import { getFootageUploadClient, needsResumableUpload } from '@/lib/footage/uploadClient';
import { holdsTheMessage, uploadingFilesContext, useFootageIntakeStore } from '@/stores/footageIntakeStore';

interface UploadedFile {
  id: string;
  originalName: string;
  resourceId: string;
  mimeType: string;
  size: number;
}

interface UploadingFile {
  id: string;
  name: string;
  size: number;
  status: 'uploading' | 'done' | 'error';
  result?: UploadedFile;
  error?: string;
}

function getFileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '')) {
    return <FileImage className="w-3 h-3 text-primary flex-shrink-0" />;
  }
  if (['pdf', 'doc', 'docx', 'txt', 'md'].includes(ext || '')) {
    return <FileText className="w-3 h-3 text-green-500 flex-shrink-0" />;
  }
  return <FileIcon className="w-3 h-3 text-muted-foreground flex-shrink-0" />;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

interface EmbeddedIvyComposerProps {
  conversationId: string;
  scene?: SceneScope | null;
}

export function EmbeddedIvyComposer({ conversationId, scene }: EmbeddedIvyComposerProps) {
  const [files, setFiles] = useState<UploadingFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const setPendingAttachments = useEmbeddedIvyChatStore(s => s.setPendingAttachments);
  const pendingVoice = useEmbeddedIvyChatStore(s => s.pendingVoice);
  const setPendingVoice = useEmbeddedIvyChatStore(s => s.setPendingVoice);

  // The next message names finished attachments and the files still going straight to storage.
  const finishedTag = useRef<string | null>(null);
  const headedToStorage = useRef<string | null>(null);
  const writeTags = useCallback(() => setPendingAttachments([finishedTag.current, headedToStorage.current].filter(Boolean).join('\n') || null), [setPendingAttachments]);

  // Upload immediately on file selection (like ChatGPT/Claude)
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;

    // Capture files BEFORE clearing the input (clearing empties the FileList reference)
    const picked = Array.from(selected);
    if (fileInputRef.current) fileInputRef.current.value = '';

    const client = getFootageUploadClient();
    const toStorage = client ? picked.filter(needsResumableUpload) : [];
    if (client && toStorage.length) {
      const intake = useFootageIntakeStore.getState();
      intake.addFiles(toStorage);
      headedToStorage.current = uploadingFilesContext(useFootageIntakeStore.getState().items);
      writeTags();
      void intake.start(conversationId, client);
    }
    const fileArray = picked.filter((f) => !toStorage.includes(f));
    if (!fileArray.length) return;

    const newFiles: UploadingFile[] = fileArray.map(f => ({
      id: `file-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: f.name,
      size: f.size,
      status: 'uploading' as const,
    }));

    setFiles(prev => [...prev, ...newFiles]);

    // Upload all files in one request
    const formData = new FormData();
    formData.append('conversationId', conversationId);
    const purposes = fileArray.map(() => 'READ_UNDERSTAND');
    formData.append('purposes', JSON.stringify(purposes));
    fileArray.forEach(f => formData.append('files', f));

    try {
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });

      if (response.data.success) {
        const results = response.data.results || [];
        setFiles(prev => prev.map(f => {
          if (!newFiles.find(nf => nf.id === f.id)) return f;
          const match = results.find((r: any) => r.originalName === f.name && r.success);
          if (match) {
            return {
              ...f,
              status: 'done' as const,
              result: {
                id: f.id,
                originalName: match.originalName,
                resourceId: match.resourceId,
                mimeType: match.mimeType,
                size: f.size,
              },
            };
          }
          return { ...f, status: 'error' as const, error: 'Upload failed' };
        }));

        // Set pending attachments for the next message
        const uploaded = results.filter((r: any) => r.success && r.resourceId);
        if (uploaded.length > 0) {
          const ctx = uploaded
            .map((a: any) => `${a.originalName} (id:${a.resourceId.slice(0, 8)})`)
            .join(', ');
          finishedTag.current = `[attachments: ${ctx}]`;
          writeTags();
        }
      } else {
        setFiles(prev => prev.map(f =>
          newFiles.find(nf => nf.id === f.id)
            ? { ...f, status: 'error' as const, error: response.data.error || 'Upload failed' }
            : f
        ));
      }
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || err?.message || 'Upload failed';
      console.error('[EmbeddedIvyComposer] Upload failed:', errMsg, err?.response?.status, err?.response?.data);
      setFiles(prev => prev.map(f =>
        newFiles.find(nf => nf.id === f.id)
          ? { ...f, status: 'error' as const, error: errMsg }
          : f
      ));
    }
  }, [conversationId, writeTags]);

  const removeFile = useCallback((id: string) => {
    setFiles(prev => {
      const updated = prev.filter(f => f.id !== id);
      // Clear pending attachments if no files left
      if (updated.filter(f => f.status === 'done').length === 0) {
        finishedTag.current = null;
        writeTags();
      } else {
        const ctx = updated
          .filter(f => f.status === 'done' && f.result)
          .map(f => `${f.result!.originalName} (id:${f.result!.resourceId.slice(0, 8)})`)
          .join(', ');
        finishedTag.current = ctx ? `[attachments: ${ctx}]` : null;
        writeTags();
      }
      return updated;
    });
  }, [writeTags]);

  // Clear files after message is sent (listen for streaming state change)
  const isStreaming = useEmbeddedIvyChatStore(s => s.isStreaming);
  const learning = useEmbeddedIvyChatStore(s => s.activeTurnKind === 'learn');
  const prevStreamingRef = useRef(false);
  if (!prevStreamingRef.current && isStreaming) {
    finishedTag.current = null;
    headedToStorage.current = null;
    // Message just started sending — clear the file pills
    if (files.length > 0) setFiles([]);
  }
  prevStreamingRef.current = isStreaming;

  const hasUploading = files.some(f => f.status === 'uploading');
  // A file still landing would be out of the agent's reach for its whole turn, so the message waits. Attaching another is fine.
  const fileLanding = useFootageIntakeStore(s => s.items.some(it => it.status === 'uploading' && holdsTheMessage(it)));
  const holdMessage = hasUploading || fileLanding;
  // Direction B (beta only): the front door's 44 px box, the same one /start shows while it works.
  const b = studioBOn();

  return (
    <div className={cn('flex-shrink-0', !b && 'bg-background')}>
      {/* Review chip stack — accepted reviewer comments awaiting send */}
      <ReviewChipStack conversationId={conversationId} />

      {/* Outline edit queue — storyboard/outline changes awaiting send */}
      <OutlineEditChipStack conversationId={conversationId} />

      <div className={b ? 'px-3 pb-3 pt-2' : 'border-t border-border-secondary px-3 py-2'}>
      {/* Pending pills — voice selection + uploaded/uploading files */}
      {(pendingVoice || files.length > 0 || (b && scene)) && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {b && scene && (
            <span data-scene-chip className="inline-flex max-w-full items-center gap-1 rounded-full border border-b-line bg-b-panel py-0.5 pl-2.5 pr-1 text-b-meta text-b-t2">
              <span className="truncate">About {scene.number ? `scene ${scene.number} · ` : ''}{scene.title}</span>
              <button type="button" aria-label="Ask about the whole video" onClick={scene.onRemove} className="grid h-5 w-5 flex-shrink-0 place-items-center rounded-full text-b-t3 transition-colors hover:bg-b-raised hover:text-b-t1">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {b && pendingVoice && (
            <span data-voice-chip className="inline-flex max-w-full items-center gap-1 rounded-full border border-b-line bg-b-panel py-0.5 pl-2.5 pr-1 text-b-meta text-b-t2">
              <span className="truncate">Voice · {pendingVoice}</span>
              <button type="button" aria-label="Remove the voice" onClick={() => setPendingVoice(null)} className="grid h-5 w-5 flex-shrink-0 place-items-center rounded-full text-b-t3 transition-colors hover:bg-b-raised hover:text-b-t1">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {b && files.map(f => (
            <span key={f.id} data-file-chip className={cn('inline-flex max-w-[240px] items-center gap-1.5 rounded-full border border-b-line bg-b-panel py-0.5 pl-2.5 pr-1 text-b-meta', f.status === 'error' ? 'text-destructive' : 'text-b-t2')}>
              {f.status === 'uploading' ? <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin" /> : getFileIcon(f.name)}
              <span className="truncate">{f.name}</span>
              <span className="text-b-t3">{formatSize(f.size)}</span>
              {f.status !== 'uploading' ? (
                <button type="button" aria-label={`Remove ${f.name}`} onClick={() => removeFile(f.id)} className="grid h-5 w-5 flex-shrink-0 place-items-center rounded-full text-b-t3 transition-colors hover:bg-b-raised hover:text-b-t1">
                  <X className="h-3 w-3" />
                </button>
              ) : <span className="w-1" />}
            </span>
          ))}
          {!b && pendingVoice && (
            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] bg-brand/10 text-brand">
              <Mic className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">Voice: {pendingVoice}</span>
              <button
                onClick={() => setPendingVoice(null)}
                className="ml-0.5 p-0.5 rounded hover:bg-brand/20 transition-colors"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          )}
          {!b && files.map(f => (
            <div
              key={f.id}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] max-w-[200px] ${
                f.status === 'error'
                  ? 'bg-destructive/10 text-destructive'
                  : f.status === 'uploading'
                    ? 'bg-muted/50 text-muted-foreground'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              {f.status === 'uploading' ? (
                <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
              ) : (
                getFileIcon(f.name)
              )}
              <span className="truncate">{f.name}</span>
              <span className="text-[10px] opacity-60">{formatSize(f.size)}</span>
              {f.status !== 'uploading' && (
                <button
                  onClick={() => removeFile(f.id)}
                  className="ml-0.5 p-0.5 rounded hover:bg-background/50 transition-colors"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <FootageChips className="mb-1.5" />

      <ComposerPrimitive.Root
        className={b
          ? 'relative flex min-h-11 items-end gap-1.5 rounded-[var(--b-radius-card)] border border-b-line bg-b-panel px-2 py-1.5 transition-colors focus-within:border-b-line-strong'
          : 'relative flex items-end gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1.5 focus-within:border-foreground/20 focus-within:ring-1 focus-within:ring-foreground/10 transition-all'}
      >
        <IconTipB label="Attach files" side="top">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={hasUploading}
            className={b
              ? 'grid h-8 w-8 flex-shrink-0 place-items-center rounded-full text-b-t3 transition-colors hover:bg-b-raised hover:text-b-t1 disabled:opacity-30'
              : 'p-1 rounded text-muted-foreground/50 hover:text-foreground hover:bg-muted transition-colors flex-shrink-0 disabled:opacity-30'}
            title="Attach files"
          >
            <Paperclip className="w-3.5 h-3.5" />
          </button>
        </IconTipB>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.txt,.md,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.svg,.mp3,.wav,.m4a,.mp4,.mov,.m4v,.webm,.mkv,.mxf,.avi,.psd,.psb,.ttf,.otf,.woff,.woff2,.csv,.json"
          onChange={handleFileSelect}
          className="hidden"
        />

        <ComposerPrimitive.Input
          autoFocus={typeof window !== 'undefined' && !window.matchMedia('(pointer: coarse)').matches}
          placeholder={b ? (isStreaming ? 'Ivy is working…' : 'Ask Ivy about this project…') : 'Ask Ivy about this project...'}
          rows={1}
          submitMode={holdMessage ? 'none' : 'enter'}
          className={b
            ? 'max-h-[120px] min-h-8 flex-1 resize-none bg-transparent py-1.5 text-b-read text-b-t1 outline-none placeholder:text-b-t3'
            : 'flex-1 bg-transparent text-[13px] text-foreground placeholder:text-muted-foreground/50 resize-none outline-none min-h-[32px] max-h-[120px] py-0.5'}
        />

        <ThreadPrimitive.If running={false}>
          <IconTipB label={holdMessage ? 'Your file is still uploading' : 'Send'} side="top">
            <ComposerPrimitive.Send asChild>
              <button
                disabled={holdMessage}
                aria-label={b ? 'Send' : undefined}
                className={b
                  ? 'grid h-8 w-8 flex-shrink-0 place-items-center rounded-full bg-b-ink text-b-on-ink transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30'
                  : 'p-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0'}
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </ComposerPrimitive.Send>
          </IconTipB>
        </ThreadPrimitive.If>

        <ThreadPrimitive.If running>
          {learning ? (
            <button
              disabled
              title="Learning from this session…"
              className="p-1.5 rounded-md bg-primary text-primary-foreground opacity-30 cursor-not-allowed flex-shrink-0"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          ) : (
            <IconTipB label="Stop" side="top">
              <ComposerPrimitive.Cancel asChild>
                <button
                  aria-label={b ? 'Stop' : undefined}
                  className={b
                    ? 'grid h-8 w-8 flex-shrink-0 place-items-center rounded-full bg-b-raised text-b-t1 transition-colors hover:bg-b-elevated'
                    : 'p-1.5 rounded-md bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors flex-shrink-0'}
                >
                  <Square className="w-3.5 h-3.5" />
                </button>
              </ComposerPrimitive.Cancel>
            </IconTipB>
          )}
        </ThreadPrimitive.If>
      </ComposerPrimitive.Root>
      </div>
    </div>
  );
}
